import { DateTime } from 'luxon';
import { ContractualMonth, FosEndLookup, MonthLookup, MonthShortLookup, OffsetLookup, OriginationDateLookup } from "./contract-month";
import { CONTRACTUAL_MONTHS } from "./contractual-months";

/**
 * @description Gets the contractual month object based on the month number.
 * @param {MonthLookup} lookup - Wrapper {@link MonthLookup} containing month as a number.
 * @returns {ContractualMonth} - {@link ContractualMonth} object.
 * @example getContractualMonth({ month: 1 }); // Returns January.
 */
export function getContractualMonth(lookup: MonthLookup): ContractualMonth; // Overload for direct month lookup

/**
 * @description Gets the contractual month object based on the current month and an offset.
 * @param {OffsetLookup} lookup - Wrapper {@link OffsetLookup} containing number of months to offset from the current month.
 * @returns {ContractualMonth} - {@link ContractualMonth} object.
 * @example getContractualMonth({ offset: 0 }); // Returns the current month.
 */
export function getContractualMonth(lookup: OffsetLookup): ContractualMonth; // Overload for offset lookup

/**
 * @description Gets the contractual month object based on the FOS end month.
 * @param {FosEndLookup} lookup - Wrapper {@link FosEndLookup} containing FOS end month.
 * @returns {ContractualMonth} - {@link ContractualMonth} object.
 */
export function getContractualMonth(fosEnd: FosEndLookup): ContractualMonth; // Overload for FOS end lookup

/**
 * @description Gets the contractual month object based on the month short name.
 * @param {MonthShortLookup} lookup - Wrapper {@link MonthShortLookup} containing month short name.
 * @returns {ContractualMonth} - {@link ContractualMonth} object.
 * @example getContractualMonth({ monthShort: "JAN" }); // Returns January contractual month object.
 */
export function getContractualMonth(monthShort: MonthShortLookup): ContractualMonth; // Overload for month short lookup

/**
 * @description Gets the contractual month object based on the month number.
 * @param {MonthLookup} lookup - Wrapper {@link MonthLookup} containing month as a number.
 * @returns {ContractualMonth} - {@link ContractualMonth} object.
 * @example getContractualMonth({ month: 1 }); // Returns January.
 */
export function getContractualMonth(originationDate: OriginationDateLookup): ContractualMonth;

export function getContractualMonth(
  lookup: MonthLookup | OffsetLookup | FosEndLookup | MonthShortLookup | OriginationDateLookup): ContractualMonth {
  const today = new Date();
  const year = today.getFullYear();

  if ("month" in lookup) {
    // Direct month lookup
    return CONTRACTUAL_MONTHS.find((contractualMonth) => parseInt(contractualMonth.asInt) === lookup.month)!;
  } 
  else if ("offset" in lookup) {
    // Offset lookup
    const currentMonthIndex = CONTRACTUAL_MONTHS.findIndex((month) => {
      const start = new Date(
        `${year}-${month.start.month.padStart(2, "0")}-${month.start.day.padStart(2, "0")}T00:00:00`
      );
      const end = new Date(`${year}-${month.end.month.padStart(2, "0")}-${month.end.day.padStart(2, "0")}T23:59:59`);
      return today >= start && today <= end;
    });

    const desiredMonthIndex =
      (currentMonthIndex + lookup.offset + CONTRACTUAL_MONTHS.length) % CONTRACTUAL_MONTHS.length;
    return CONTRACTUAL_MONTHS[desiredMonthIndex];
  } 
  else if ("fosEnd" in lookup) {
    // FOS Month End Lookup
    const fosEndMonth = lookup.fosEnd;
    // find the CONTRACTUAL_MONTHS that has start.fosEnd === fosEndMonth
    return CONTRACTUAL_MONTHS.find((contractualMonth) => contractualMonth.end.fosEnd === fosEndMonth)!;
  } 
  else if ("monthShort" in lookup) {
    // Month short lookup
    return CONTRACTUAL_MONTHS.find((contractualMonth) => contractualMonth.monthShort === lookup.monthShort)!;
  }
  else {
     const originationDate = DateTime.fromISO(lookup.originationDate, { zone: 'utc' });
     const currentMonthIndex = CONTRACTUAL_MONTHS.findIndex((month) => {
          const start = DateTime.fromObject({
            year: originationDate.year,
            month: +month.start.month.padStart(2, "0"),
            day: +month.start.day.padStart(2, "0")
          }, { zone: 'utc' });
          const end = DateTime.fromObject({
            year: originationDate.year,
            month: +month.end.month.padStart(2, "0"),
            day: +month.end.day.padStart(2, "0")
          }, { zone: 'utc' });
          return (originationDate >= start && originationDate <= end);
     });
     return CONTRACTUAL_MONTHS[currentMonthIndex];
  }
}
 